import React from 'react'

interface CheckboxOptionProps {
  name: string
  value: string
  label: string
  checked: boolean
  onChange: (value: string, checked: boolean) => void
  hasOtherField?: boolean
  otherValue?: string
  onOtherChange?: (value: string) => void
}

/**
 * Checkbox option card for multiple-choice survey questions.
 *
 * Renders a visually-hidden native <input type="checkbox"> inside a styled
 * <label> so the entire card is clickable. When checked, shows a purple
 * border and subtle background. When `hasOtherField` is true and the
 * option is checked, an inline text input appears for free-form entry.
 */
const CheckboxOption: React.FC<CheckboxOptionProps> = ({
  name,
  value,
  label,
  checked,
  onChange,
  hasOtherField = false,
  otherValue = '',
  onOtherChange,
}) => {
  const inputId = `${name}-${value}`

  return (
    <div>
      <label
        htmlFor={inputId}
        className={`
          flex items-center gap-3 w-full px-4 py-3 rounded-lg border cursor-pointer
          transition-all duration-200
          ${
            checked
              ? 'border-violet-600 bg-violet-50 dark:bg-violet-500/10'
              : 'border-slate-200 dark:border-border bg-background hover:border-slate-300 dark:hover:border-border hover:bg-slate-50 dark:hover:bg-accent'
          }
        `}
      >
        <input
          type="checkbox"
          id={inputId}
          name={name}
          value={value}
          checked={checked}
          onChange={(e) => onChange(value, e.target.checked)}
          className="sr-only"
        />

        {/* Custom checkbox indicator */}
        <span
          className={`
            flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center
            transition-colors duration-200
            ${checked ? 'border-violet-600 bg-violet-600' : 'border-slate-300 dark:border-border'}
          `}
        >
          {checked && (
            <svg
              className="w-3 h-3 text-white"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          )}
        </span>

        <span
          className={`text-sm ${checked ? 'text-slate-900 dark:text-foreground font-medium' : 'text-slate-700 dark:text-muted-foreground'}`}
        >
          {label}
        </span>
      </label>

      {/* "Other" free-form text input, shown when this option is checked */}
      {hasOtherField && checked && (
        <input
          type="text"
          value={otherValue}
          onChange={(e) => onOtherChange?.(e.target.value)}
          placeholder="Especifique..."
          className="mt-2 ml-8 w-[calc(100%-2rem)] px-3 py-2 text-sm border border-slate-200 dark:border-border rounded-lg
            focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent
            placeholder:text-slate-400 dark:placeholder:text-muted-foreground dark:bg-accent dark:text-foreground"
        />
      )}
    </div>
  )
}

export default CheckboxOption
